import { createContext, forwardRef, useContext } from 'react'

import { Accordion as AccordionPrimitive } from '@base-ui/react/accordion'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon/icon'
import { textVariants } from '../../atoms/text/text'

const accordionVariants = tv(
  {
    slots: {
      /* Choreography ported from motion.dev's Radix Accordion example: panel
       * height, collapse mask, content blur/fade and caret spin all run on
       * one spring (visualDuration 0.4s, bounce 0.2). The literal is
       * String(spring(0.4, 0.2)) from `motion`, frozen so the component
       * ships no JS animation code — regenerate it if the spring changes,
       * together with its verbatim copy `--breadcrumb-spring` in
       * molecules/breadcrumb. */
      root: '[--accordion-spring:750ms_linear(0,0.0671,0.215,0.3869,0.5505,0.69,0.8,0.8814,0.9381,0.975,0.9972,1.0091,1.0142,1.0151,1.0138,1.0114,1.0088,1.0064,1.0043,1.0028,1.0016,1.0008,1.0003,1,1)]',
      item: 'border-surface-200 border-b py-4 first:pt-0 last:border-b-0 last:pb-0',
      header: 'flex',
      trigger: [
        'group/accordion-trigger flex w-full items-center gap-2 text-left',
        'text-surface-title-50 transition-colors duration-300 ease-out',
        'data-[panel-open]:text-bright-blue',
        'focus-visible:ring-primary-200 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
      ],
      title: 'min-w-0 flex-1 [word-break:break-word]',
      caret: [
        /* Figma: the icon frame equals the title's line-height and the glyph
         * em is 8/9 of the frame (24px inside a 27px frame on the desktop
         * tier). Deriving both from the line-height token keeps the pair
         * correct on every responsive tier of the Ømni matrix. */
        'flex shrink-0 items-center justify-center',
        'size-(--accordion-caret) text-[length:calc(var(--accordion-caret)*8/9)]',
        'group-data-[panel-open]/accordion-trigger:rotate-180',
        'motion-safe:[transition:rotate_var(--accordion-spring)]'
      ],
      panel: [
        'group/accordion-panel overflow-hidden',
        'h-[var(--accordion-panel-height)] data-[ending-style]:h-0 data-[starting-style]:h-0',
        /* Collapse mask: a black→transparent gradient twice the panel's
         * height. At mask-position 0% the panel sits inside the all-black
         * half (no visible masking); sliding to 100% while collapsed fades
         * the bottom half of the visible window. mask-position interpolates
         * natively, so no registered @property is needed. */
        '[mask-image:linear-gradient(to_bottom,black_75%,transparent_100%)] [mask-position:0%_0%] [mask-size:100%_200%]',
        'data-[ending-style]:[mask-position:0%_100%] data-[starting-style]:[mask-position:0%_100%]',
        'motion-safe:[transition:height_var(--accordion-spring),mask-position_var(--accordion-spring)]'
      ],
      content: [
        'text-surface-body pt-2',
        'group-data-[starting-style]/accordion-panel:opacity-0 group-data-[starting-style]/accordion-panel:blur-[2px]',
        'group-data-[ending-style]/accordion-panel:opacity-0 group-data-[ending-style]/accordion-panel:blur-[2px]',
        'motion-safe:[transition:filter_var(--accordion-spring),opacity_var(--accordion-spring)]'
      ]
    },
    variants: {
      size: {
        md: {
          trigger: textVariants({ variant: 'body-xl', weight: 'semibold' }),
          content: textVariants({ variant: 'body-md' }),
          caret: '[--accordion-caret:var(--text-body-xl--line-height)]'
        },
        sm: {
          trigger: textVariants({ variant: 'body-md', weight: 'semibold' }),
          content: textVariants({ variant: 'caption-lg' }),
          caret: '[--accordion-caret:var(--text-body-md--line-height)]'
        }
      }
    },
    defaultVariants: { size: 'md' }
  },
  { twMerge: false }
)

/* root/item/header/title take no variants — resolve their classes once. */
const staticSlots = accordionVariants()
const rootClass = staticSlots.root()
const itemClass = staticSlots.item()
const headerClass = staticSlots.header()
const titleClass = staticSlots.title()

export type AccordionSize = NonNullable<
  VariantProps<typeof accordionVariants>['size']
>

const AccordionSizeContext = createContext<AccordionSize>('md')

/* `multiple` is omitted on purpose: the Ømni UX keeps at most one item
 * open, so the primitive's multi-open mode is not part of the API. */
export type AccordionProps = Omit<AccordionPrimitive.Root.Props, 'multiple'> & {
  /** Type scale for every item: md — 18px titles / 14px body,
   *  sm — 14px titles / 12px body. */
  size?: AccordionSize
}

export type AccordionItemProps = AccordionPrimitive.Item.Props

export type AccordionTriggerProps = AccordionPrimitive.Trigger.Props

export type AccordionPanelProps = AccordionPrimitive.Panel.Props

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  ({ size = 'md', className, ...props }, ref) => (
    <AccordionSizeContext.Provider value={size}>
      <AccordionPrimitive.Root
        ref={ref}
        data-slot='accordion'
        className={cn(rootClass, className)}
        {...props}
      />
    </AccordionSizeContext.Provider>
  )
)
Accordion.displayName = 'Accordion'

export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, ...props }, ref) => (
    <AccordionPrimitive.Item
      ref={ref}
      data-slot='accordion-item'
      className={cn(itemClass, className)}
      {...props}
    />
  )
)
AccordionItem.displayName = 'AccordionItem'

export const AccordionTrigger = forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ className, children, ...props }, ref) => {
  const size = useContext(AccordionSizeContext)
  const { trigger, caret } = accordionVariants({ size })

  return (
    <AccordionPrimitive.Header
      data-slot='accordion-header'
      className={headerClass}
    >
      <AccordionPrimitive.Trigger
        ref={ref}
        data-slot='accordion-trigger'
        className={cn(trigger(), className)}
        {...props}
      >
        <span className={titleClass}>{children}</span>
        {/* aria-hidden: the Material Symbols ligature is real text and would
            otherwise leak into the trigger's accessible name. */}
        <Icon name='keyboard_arrow_down' aria-hidden className={caret()} />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
})
AccordionTrigger.displayName = 'AccordionTrigger'

export const AccordionPanel = forwardRef<HTMLDivElement, AccordionPanelProps>(
  ({ className, children, ...props }, ref) => {
    const size = useContext(AccordionSizeContext)
    const { panel, content } = accordionVariants({ size })

    return (
      <AccordionPrimitive.Panel
        ref={ref}
        data-slot='accordion-panel'
        className={cn(panel(), className)}
        {...props}
      >
        {/* The 8px title↔body gap lives INSIDE the measured panel height so
            it expands/collapses with the spring instead of popping. */}
        <div data-slot='accordion-content' className={content()}>
          {children}
        </div>
      </AccordionPrimitive.Panel>
    )
  }
)
AccordionPanel.displayName = 'AccordionPanel'
